import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../api/client';
import { formatMkd } from '../lib/format';
import BarChart from '../components/charts/BarChart';
import { BUSINESS_COLOR } from '../components/charts/palette';

/**
 * Fitimet — Stargroup comparison. Ports frmStargroupFitimet.
 *
 * Only the three businesses that record revenue appear; Stargroup has no rows
 * of its own, it is the sum of these.
 */

interface MonthPoint {
  year: number;
  month: number;
  monthName: string;
  values: Record<string, number>;
}

const REVENUE_BUSINESSES = [
  { slug: 'stargym', name: 'Stargym' },
  { slug: 'missfit', name: 'Missfit' },
  { slug: 'starbar', name: 'Starbar' },
];

export default function FitimetKrahasimi() {
  const [months, setMonths] = useState(6);

  const dataQuery = useQuery({
    queryKey: ['fitimet-krahasimi', months],
    queryFn: () => apiGet<{ months: MonthPoint[] }>('/fitimet/krahasimi', { months }),
    placeholderData: (prev) => prev,
  });

  const points = dataQuery.data?.months ?? [];
  const labels = points.map((p) => `${p.monthName.slice(0, 3)} ${p.year}`);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Fitimet — krahasimi</h1>
          <p className="text-sm text-muted">Të ardhurat e çdo biznesi, muaj pas muaji</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted" htmlFor="kr-months">
            Periudha
          </label>
          <select
            id="kr-months"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            <option value={6}>6 muajt e fundit</option>
            <option value={12}>12 muajt e fundit</option>
            <option value={24}>24 muajt e fundit</option>
          </select>
        </div>
      </div>

      <section className="rounded-xl bg-white p-4">
        {dataQuery.isLoading ? (
          <div className="flex h-52 items-center justify-center text-sm text-muted">
            Duke ngarkuar…
          </div>
        ) : (
          <BarChart
            ariaLabel="Fitimet sipas biznesit dhe muajit"
            labels={labels}
            series={REVENUE_BUSINESSES.map((b) => ({
              key: b.slug,
              label: b.name,
              color: BUSINESS_COLOR[b.slug]!,
              values: points.map((p) => p.values[b.slug] ?? 0),
            }))}
          />
        )}
      </section>

      {/* The same figures as numbers — identity and value never colour-alone. */}
      <section className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-2.5 font-semibold">Muaji</th>
                {REVENUE_BUSINESSES.map((b) => (
                  <th key={b.slug} className="px-4 py-2.5 text-right font-semibold">
                    {b.name}
                  </th>
                ))}
                <th className="px-4 py-2.5 text-right font-semibold">Totali</th>
              </tr>
            </thead>
            <tbody>
              {points
                .slice()
                .reverse()
                .map((p) => {
                  const rowTotal = REVENUE_BUSINESSES.reduce(
                    (s, b) => s + (p.values[b.slug] ?? 0),
                    0,
                  );
                  return (
                    <tr key={`${p.year}-${p.month}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2">
                        {p.monthName} {p.year}
                      </td>
                      {REVENUE_BUSINESSES.map((b) => (
                        <td key={b.slug} className="tnum px-4 py-2 text-right">
                          {formatMkd(p.values[b.slug] ?? 0)}
                        </td>
                      ))}
                      <td className="tnum px-4 py-2 text-right font-semibold">
                        {formatMkd(rowTotal)}
                      </td>
                    </tr>
                  );
                })}
              {points.length === 0 && !dataQuery.isLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted">
                    Nuk ka të dhëna për këtë periudhë.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
